import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Alert,
  CircularProgress
} from "@mui/material";
import { AwardArea } from "../types/awardarea";
import { useAddAwardAreaMutation, useUpdateAwardAreaMutation } from "../api/awardareaApi";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  awardArea?: AwardArea | null;
}

const AddEditAwardArea: React.FC<Props> = ({ open, setModalOpen, onSuccess, awardArea }) => {
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
  }>({
    name: "",
    description: "",
  });

  const [createAwardArea] = useAddAwardAreaMutation();
  const [updateAwardArea] = useUpdateAwardAreaMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (awardArea) {
      setFormData({
        name: awardArea.name,
        description: awardArea.description,
      });
    } else {
      setFormData({
        name: "",
        description: "",
      });
    }
  }, [awardArea, open]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const body = {
      name: formData.name,
      description: formData.description,
    };
    try {
      if (awardArea?.id) {
        await updateAwardArea({ id: awardArea.id, body }).unwrap();
      } else {
        await createAwardArea({ body }).unwrap();
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
    } catch (err) {
      setSubmissionError("Failed to save award area. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseError = () => {
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{awardArea ? "Edit Award Area" : "Add Award Area"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Description"
          multiline
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
        {submissionError && (
          <Alert severity="error" onClose={handleCloseError} sx={{ mt: 2 }}>
            {submissionError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSubmitting}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditAwardArea;